/**
 * FieldContext — internal context plumbing for <Field>.
 *
 * Lets <Input>, <Textarea>, <Select>, etc. inherit the auto-generated id,
 * aria-describedby (helper + error), aria-invalid, aria-required, and
 * disabled state from the parent <Field> without callers wiring them
 * manually.
 *
 * When NO Field is present, useFieldContext() returns null and the input
 * primitives fall back to props the caller passed directly. This is the
 * "library mode" for primitives used outside Field.
 */

import { createContext, useContext } from 'react';

export interface FieldContextValue {
  inputId: string;
  helperId?: string;
  errorId?: string;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

/**
 * Resolve the aria-describedby string from helper/error ids.
 * Helpful so input primitives don't have to reimplement the join.
 */
export function describedByFromContext(ctx: FieldContextValue | null, fallback?: string): string | undefined {
  if (!ctx) return fallback;
  const ids = [ctx.helperId, ctx.errorId].filter(Boolean) as string[];
  if (fallback) ids.push(fallback);
  return ids.length > 0 ? ids.join(' ') : undefined;
}
